import React from 'react';

import type { UUID } from '@aline/common';
import { Typography } from '@mui/material';
import { ListTodo, Plus, Signature } from 'lucide-react';
import { AlineInput } from 'src/AlineInput';
import { AlineButton } from 'src/components';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { useAddTaskComposer } from './AddTaskInput';
import { useAddPhase } from './use-add-phase';

const useStyles = makeStyles()(theme => ({
  action: {
    marginTop: 'auto',
    paddingTop: theme.spacing(2.5),
    width: '100%',
  },
  addIcon: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  card: {
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    border: '1px dashed #cbd5e1',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    padding: theme.spacing(3.5, 3),
    textAlign: 'center',
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: 'rgba(20, 110, 232, 0.1)',
    borderRadius: '50%',
    color: theme.appColors.primary,
    display: 'flex',
    height: 48,
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
    width: 48,
  },
  input: {
    backgroundColor: theme.palette.common.white,
  },
  root: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  },
  signingButton: {
    height: 40,
  },
  signingIconCircle: {
    backgroundColor: 'rgba(22, 163, 74, 0.1)',
    color: '#16a34a',
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: theme.spacing(0.5),
  },
  subtitleLine: {
    display: 'block',
  },
  title: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
  },
}));

const COPY = {
  template: {
    signing: {
      lines: ['Start with a signing phase.', 'You can add tasks before it anytime.'],
      title: 'Template for signing',
    },
    tasks: {
      lines: ['List the steps your team repeats.', 'Each new workflow starts with them.'],
      title: 'Template with tasks',
    },
  },
  workflow: {
    signing: {
      lines: [
        'Skip tasks and send documents for signature.',
        'You can add tasks before signing later.',
      ],
      title: 'Go straight to signing',
    },
    tasks: {
      lines: ['Break the work into steps.', 'Assign each to a teammate or Aline AI.'],
      title: 'Start with tasks',
    },
  },
} as const;

interface EmptyTaskListProps {
  autoFocus: boolean;
  /** False once the workflow has a signing phase, which also means it is not empty. */
  canAddSigning: boolean;
  isTemplate: boolean;
  onCreated: (task: ApolloTypes.TaskSimpleFragment) => void;
  onDismiss: () => void;
  onPhaseCreated: (id: string) => void;
  /** The workflow's phase. Null when it has none yet; the first task creates it. */
  phaseId: UUID | null;
  workflowId: UUID;
}

/**
 * A workflow or template with no tasks yet. Offers the two common starts side by side: a list of
 * tasks, or going straight to signatures.
 */
export function EmptyTaskList({
  autoFocus,
  canAddSigning,
  isTemplate,
  onCreated,
  onDismiss,
  onPhaseCreated,
  phaseId,
  workflowId,
}: EmptyTaskListProps): JSX.Element {
  const { classes, cx } = useStyles();
  const composer = useAddTaskComposer({ onCreated, onDismiss, phaseId, workflowId });
  const { addPhase, loading: addingSigning } = useAddPhase(workflowId);
  const copy = isTemplate ? COPY.template : COPY.workflow;

  const addSigningPhase = async () => {
    const id = await addPhase({ isSigning: true });
    if (id) onPhaseCreated(id);
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.iconCircle}>
          <ListTodo size={24} />
        </div>
        <Typography className={classes.title}>{copy.tasks.title}</Typography>
        <SubtitleLines lines={copy.tasks.lines} />
        <div className={classes.action}>
          <AlineInput
            aria-label="First task title"
            autoFocus={autoFocus}
            inputClassName={classes.input}
            placeholder="Type a task and hit enter..."
            startAdornment={<Plus className={classes.addIcon} size={16} />}
            value={composer.title}
            onBlur={composer.onBlur}
            onChange={composer.onChange}
            onKeyDown={composer.onKeyDown}
          />
        </div>
      </div>
      {canAddSigning && (
        <div className={classes.card}>
          <div className={cx(classes.iconCircle, classes.signingIconCircle)}>
            <Signature size={24} />
          </div>
          <Typography className={classes.title}>{copy.signing.title}</Typography>
          <SubtitleLines lines={copy.signing.lines} />
          <div className={classes.action}>
            <AlineButton
              buttonClassName={classes.signingButton}
              color="primary"
              loading={addingSigning}
              startIcon={<Signature size={16} />}
              variant="outlined"
              onClick={() => void addSigningPhase()}
            >
              Add Signing Phase
            </AlineButton>
          </div>
        </div>
      )}
    </div>
  );
}

function SubtitleLines({ lines }: { lines: readonly string[] }) {
  const { classes } = useStyles();

  return (
    <Typography className={classes.subtitle}>
      {lines.map(line => (
        <span key={line} className={classes.subtitleLine}>
          {line}
        </span>
      ))}
    </Typography>
  );
}
